import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import Spinner from "aldoo-ra/Spinner"
import { showNotification } from "aldoo-ra/Notification"
import { ArrowRightIcon, ArrowLeftIcon, ComputerDesktopIcon, InformationCircleIcon, LockClosedIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { GetParam } from "aldoo-ra/Admin/ParamIO/api"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { UploadContentToEnvironment, DownloadContentFromEnvironment } from "./api"
import getRealtimeTaskController from "aldoo-ra/TaskProgress/realtime-task-controller"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import InstallProgressSteps from "./InstallProgressSteps"
import PubSub from "pubsub-js"

const taskController = getRealtimeTaskController()

// Helper function to format type names with spaces
const formatTypeName = (typeName, isCMSTypeSchema) => {
  // If it's a CMS Type schema, always show "CMS Type"
  if (isCMSTypeSchema || typeName === "CMSType" || typeName === "ContentType") {
    return "CMS Type"
  }
  // Insert space before capital letters (BlogPost -> Blog Post)
  return typeName.replace(/([A-Z])/g, ' $1').trim()
}

const DB_MODES = [
  {
    label: "Skip",
    value: "SKIP",
    description: "Don't modify existing database records. Only add new records that don't exist."
  },
  {
    label: "Update",
    value: "UPDATE",
    description: "Update existing database records only. Don't add new records."
  },
  {
    label: "Merge",
    value: "MERGE",
    description: "Add new database records and keep existing ones unchanged. Best for adding content without overwriting."
  },
  {
    label: "Replace",
    value: "REPLACE",
    description: "Replace all matching database records with the update content. Existing data will be overwritten."
  },
]

const FILE_MODES = [
  {
    label: "Skip",
    value: "SKIP",
    description: "Don't copy files that already exist. Only add new files that are missing."
  },
  {
    label: "Replace",
    value: "REPLACE",
    description: "Overwrite existing files with the update files. Existing files will be replaced."
  },
]

export default function EnvironmentContentInstallDialog({ content, contentType, target: targetProp, contentName, onClose }) {
  const [environments, setEnvironments] = useState([])
  const [selectedEnvironment, setSelectedEnvironment] = useState(null)
  const [dbMode, setDbMode] = useState(DB_MODES[1]) // Default to "Update"
  const [fileMode, setFileMode] = useState(FILE_MODES[0]) // Default to "Skip"
  const [direction, setDirection] = useState(() => {
    // Load saved direction from LocalUserPreferences
    return LocalUserPreferences.get("admin.updates.environments.direction") || "upload"
  })

  // Detect if this is a CMS Type schema
  const isCMSTypeSchema = contentType === "CMSType" || contentType === "ContentType"
  // Multi-target mode when targetProp is provided directly
  const isMultiTarget = !!targetProp
  const displayName = contentName || content?.name || ""
  const [loading, setLoading] = useState(true)
  const [installing, setInstalling] = useState(false)
  const [installProgress, setInstallProgress] = useState(null)

  // Prevent closing while installing
  const handleClose = () => {
    if (installing) {
      return // Don't close while installing
    }
    onClose()
  }
  const [installSteps, setInstallSteps] = useState({
    creating: { status: 'pending', label: 'Creating Update' },
    sending: { status: 'pending', label: 'Sending Update' },
    installing: { status: 'pending', label: 'Installing Update' },
  })
  const [currentStep, setCurrentStep] = useState(null)
  const [currentTaskName, setCurrentTaskName] = useState(null)
  const unsubscribeRef = useRef(null)

  useEffect(() => {
    loadEnvironments()

    // Cleanup: unsubscribe from events when component unmounts
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
    }
  }, [])

  const loadEnvironments = async () => {
    try {
      const response = await GetParam("SITE_ENVIRONMENTS")
      if (response?.value && Array.isArray(response.value)) {
        const envOptions = response.value.map((env) => ({
          label: env.name,
          value: env.id,
          data: env,
          disabled: env.allowUpdates === false,
          icon: env.allowUpdates === false ? <LockClosedIcon className="w-4 h-4" /> : null,
        }))
        setEnvironments(envOptions)

        // Try to restore last selected environment from LocalUserPreferences
        let lastSelectedId = LocalUserPreferences.get("admin.updates.environments.lastSelected")

        // Handle if the saved value is an array (old format) - extract the ID
        if (Array.isArray(lastSelectedId) && lastSelectedId.length > 0) {
          lastSelectedId = lastSelectedId[0]?.value || lastSelectedId[0]?.id
        }

        const lastSelected = envOptions.find(opt => opt.value === lastSelectedId)

        // Set to last selected if it exists, otherwise first option
        if (lastSelected) {
          setSelectedEnvironment(lastSelected)
        } else if (envOptions.length > 0) {
          setSelectedEnvironment(envOptions[0])
        }
      }
    } catch (error) {
      console.error("Error loading environments:", error)
      showNotification({
        message: "Failed to load environments",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleDirection = () => {
    setDirection((prev) => {
      const newDirection = prev === "upload" ? "download" : "upload"
      LocalUserPreferences.set("admin.updates.environments.direction", newDirection)
      return newDirection
    })
    // Reset progress when direction changes
    resetProgress()
  }

  const resetProgress = () => {
    setInstallProgress(null)
    setInstallSteps({
      creating: { status: 'pending', label: 'Creating Update' },
      sending: { status: 'pending', label: 'Sending Update' },
      installing: { status: 'pending', label: 'Installing Update' },
    })
    setCurrentStep(null)
    setCurrentTaskName(null)
    // Unsubscribe from any active task
    if (unsubscribeRef.current) {
      unsubscribeRef.current()
      unsubscribeRef.current = null
    }
  }

  const handleInstall = async () => {
    if (!selectedEnvironment || !selectedEnvironment.data) {
      showNotification({
        message: "Please select an environment",
        className: "bg-admin_error text-white",
      })
      return
    }

    setInstalling(true)
    setInstallProgress({ status: "running", name: "Preparing installation", progress: 0 })

    try {
      // Use pre-built target (multi-selection mode) or build from single content item
      let target
      if (isMultiTarget) {
        target = targetProp
      } else {
        const dbResolver = DatabaseModelTypeResolver()
        const isDBModel = dbResolver.getType(contentType) !== null
        const isCMSType = contentType === "CMSType" || contentType === "ContentType"

        if (isCMSType) {
          target = [{ type: "CMSType", match: { "_id": content._id } }]
        } else if (isDBModel) {
          target = [{ type: contentType, match: { "_id": content._id } }]
        } else {
          target = [{ type: "CMSContent", match: { "content.type": contentType, "_id": content._id } }]
        }
      }

      const contentMeta = isMultiTarget
        ? { _id: null, name: displayName, type: "selection" }
        : { _id: content._id, name: content.name, type: contentType }

      let response
      if (direction === "upload") {
        response = await UploadContentToEnvironment(
          {
            environment: selectedEnvironment.data,
            content: contentMeta,
            target,
            DB_MODE: dbMode?.value?.toLowerCase() || "update",
            FILE_MODE: fileMode?.value?.toLowerCase() || "replace",
          },
          (progress) => { setInstallProgress(progress) }
        )
      } else {
        response = await DownloadContentFromEnvironment(
          {
            environment: selectedEnvironment.data,
            content: contentMeta,
            target,
            DB_MODE: dbMode?.value?.toLowerCase() || "update",
            FILE_MODE: fileMode?.value?.toLowerCase() || "skip",
          },
          (progress) => { setInstallProgress(progress) }
        )
      }

      // The devops API returns a task ID
      if (response && response.task) {
        const taskId = response.task

        // Subscribe to real-time task updates
        unsubscribeRef.current = taskController.subscribe(taskId, (taskData) => {
          setInstallProgress({
            status: taskData.status,
            name: taskData.name,
            progress: taskData.progress,
            error: taskData.error,
            result: taskData.result
          })

          // Update current task name for display
          setCurrentTaskName(taskData.name)

          // Determine which step we're in based on task name
          const taskName = taskData.name?.toLowerCase() || ''

          // Creating step: archive creation, dependency resolution
          if (taskName.includes('creating') || taskName.includes('archive') || taskName.includes('dependencies') || taskName.includes('generating')) {
            if (currentStep !== 'creating') {
              setCurrentStep('creating')
              setInstallSteps(prev => ({
                ...prev,
                creating: { ...prev.creating, status: 'active' }
              }))
            }
          }
          // Sending step: upload to remote, getting acknowledgment
          else if (taskName.includes('upload') || taskName.includes('sending') || taskName.includes('remote environment')) {
            if (currentStep !== 'sending') {
              setCurrentStep('sending')
              setInstallSteps(prev => ({
                ...prev,
                creating: { ...prev.creating, status: 'completed' },
                sending: { ...prev.sending, status: 'active' }
              }))
            }
          }
          // Installing step: remote server installing the update
          else if (taskName.includes('installing') || taskName.includes('tracking') || taskName.includes('remote task') || taskName.includes('import') || taskName.includes('unpack')) {
            if (currentStep !== 'installing') {
              setCurrentStep('installing')
              setInstallSteps(prev => ({
                ...prev,
                creating: { ...prev.creating, status: 'completed' },
                sending: { ...prev.sending, status: 'completed' },
                installing: { ...prev.installing, status: 'active' }
              }))
            }
          }

          // Check if task is completed
          if (taskData.status === 'completed') {
            // Mark all steps as completed
            setInstallSteps({
              creating: { label: 'Creating Update', status: 'completed' },
              sending: { label: 'Sending Update', status: 'completed' },
              installing: { label: 'Installing Update', status: 'completed' },
            })
            setCurrentStep('completed')

            setInstalling(false)

            // Publish event if download completed successfully (single-item mode only)
            if (direction === "download" && !isMultiTarget && content?._id) {
              PubSub.publish("content-download-completed", {
                contentId: content._id,
                contentType: contentType
              })
            }

            // Don't auto-close - let user close manually after reviewing the completion
            // Unsubscribe from task updates
            if (unsubscribeRef.current) {
              unsubscribeRef.current()
              unsubscribeRef.current = null
            }
          } else if (taskData.status === 'error') {
            showNotification({
              message: `Installation failed: ${taskData.error || 'Unknown error'}`,
              className: "bg-admin_error text-white",
            })
            setInstalling(false)
          }
        })
      } else if (response && response.error) {
        throw new Error(response.error)
      } else {
        throw new Error(`Unexpected response format: ${JSON.stringify(response)}`)
      }
    } catch (error) {
      console.error("Installation error:", error)
      showNotification({
        message: `Failed to install: ${error.message}`,
        className: "bg-admin_error text-white",
      })
      setInstallProgress(null)
      setInstalling(false)
    }
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={handleClose} variant="admin_centered" title="Install Content">
        <div className="p-6 text-center text-admin_text_secondary">Loading environments...</div>
      </Modal>
    )
  }

  if (environments.length === 0) {
    return (
      <Modal isOpen={true} onClose={handleClose} variant="admin_centered" title="Install Content">
        <div className="p-6 text-center">
          <Typography variant="admin_txt1" className="text-admin_text_secondary mb-4">
            No environments configured. Please add an environment first.
          </Typography>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={true} onClose={handleClose} variant="admin_centered" title="Install Content">
      <div className="p-6 space-y-6 min-w-[600px]">
        {/* Content Name */}
        <div className="flex items-center justify-center gap-3">
          {!isMultiTarget && contentType && (
            <span className="px-3 py-1 bg-admin_primary/10 text-admin_primary text-sm font-medium rounded-full border border-admin_primary/20">
              {formatTypeName(contentType, isCMSTypeSchema)}
            </span>
          )}
          <Typography variant="admin_txt1" className="text-admin_text font-medium">
            {displayName}
          </Typography>
        </div>

        {/* Direction Selector */}
        <div className="flex items-stretch gap-0 py-6">
          <div className="flex flex-col items-center justify-center gap-1 w-20 bg-admin_canvas rounded-l-lg border-2 border-admin_bordered border-r-0 min-h-[80px]">
            <ComputerDesktopIcon className="w-8 h-8 text-admin_text" />
            <Typography variant="admin_txt2" className="text-admin_text_secondary">
              Content
            </Typography>
          </div>

          <button
            onClick={toggleDirection}
            className={`flex flex-col items-center justify-center gap-2 w-32 hover:bg-admin_canvas transition-colors border-y-2 cursor-pointer group ${
              direction === "upload"
                ? "bg-admin_success/20 border-admin_success/50"
                : "bg-admin_error/20 border-admin_error/50"
            }`}
          >
            {direction === "upload" ? (
              <>
                <ArrowRightIcon className="w-8 h-8 text-admin_success group-hover:text-admin_primary transition-colors" />
                <Typography variant="admin_txt2" className="text-admin_success group-hover:text-admin_text transition-colors">
                  Upload
                </Typography>
              </>
            ) : (
              <>
                <ArrowLeftIcon className="w-8 h-8 text-admin_error group-hover:text-admin_primary transition-colors" />
                <Typography variant="admin_txt2" className="text-admin_error group-hover:text-admin_text transition-colors">
                  Download
                </Typography>
              </>
            )}
          </button>

          <div className="flex-1 flex items-center px-4 bg-admin_paper rounded-r-lg border-2 border-admin_bordered">
            <DropdownSelect
              variant="admin"
              options={environments}
              value={selectedEnvironment}
              onChange={(value) => {
                // DropdownSelect returns an array even in single-selection mode
                const selected = Array.isArray(value) ? value[0] : value
                setSelectedEnvironment(selected)
                // Save selection to LocalUserPreferences
                if (selected?.value) {
                  LocalUserPreferences.set("admin.updates.environments.lastSelected", selected.value)
                }
                // Reset progress when environment changes
                resetProgress()
              }}
              placeholder="Select environment"
              showClearButton={false}
              showCheckbox={false}
              showSelectionTick={true}
              onValueItemRender={(item) => {
                if (!item) return null
                return (
                  <div className="flex items-center gap-2">
                    {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                    <span>{item.label}</span>
                  </div>
                )
              }}
              className="w-full"
            />
          </div>
        </div>

        {/* Progress Display - Step by Step - Always visible */}
        <InstallProgressSteps
          steps={installSteps}
          currentStep={currentStep}
          currentTaskName={currentTaskName}
          status={installProgress?.status}
          error={installProgress?.error}
        />

      </div>

      {/* Footer */}
      <div className="p-6 border-t border-admin_bordered">
        <div className="flex items-end gap-4">
          <div className="w-64">
            <div className="flex items-center gap-1.5 mb-2">
              <Typography variant="admin_label" className="text-admin_text mb-0">
                Database Mode
              </Typography>
              <Tooltip
                variant="admin"
                content={
                  <div className="space-y-2 max-w-xs">
                    {DB_MODES.map(mode => (
                      <div key={mode.value}>
                        <div className="font-semibold">{mode.label}</div>
                        <div className="text-sm opacity-90">{mode.description}</div>
                      </div>
                    ))}
                  </div>
                }
              >
                <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_text cursor-help flex-shrink-0" />
              </Tooltip>
            </div>
            <DropdownSelect
              variant="admin"
              options={DB_MODES}
              value={dbMode}
              onChange={setDbMode}
              placeholder="Select mode"
              showClearButton={false}
              localUserPreferencesKey="admin.updates.environments.dbMode"
              disabled={installing}
            />
          </div>
          <div className="w-64">
            <div className="flex items-center gap-1.5 mb-2">
              <Typography variant="admin_label" className="text-admin_text mb-0">
                File Mode
              </Typography>
              <Tooltip
                variant="admin"
                content={
                  <div className="space-y-2 max-w-xs">
                    {FILE_MODES.map(mode => (
                      <div key={mode.value}>
                        <div className="font-semibold">{mode.label}</div>
                        <div className="text-sm opacity-90">{mode.description}</div>
                      </div>
                    ))}
                  </div>
                }
              >
                <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_text cursor-help flex-shrink-0" />
              </Tooltip>
            </div>
            <DropdownSelect
              variant="admin"
              options={FILE_MODES}
              value={fileMode}
              onChange={setFileMode}
              placeholder="Select mode"
              showClearButton={false}
              localUserPreferencesKey="admin.updates.environments.fileMode"
              disabled={installing}
            />
          </div>
          <div className="flex-1"></div>
          {selectedEnvironment?.data?.allowUpdates === false ? (
            <Tooltip text="This environment has been locked for updates" variant="admin" position="top">
              <Button
                variant="admin_primary"
                onClick={handleInstall}
                className="min-w-[200px]"
                disabled={true}
              >
                <div className="flex items-center gap-2 justify-center">
                  <LockClosedIcon className="w-4 h-4" />
                  <span>Install</span>
                </div>
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="admin_primary"
              onClick={handleInstall}
              className="min-w-[200px]"
              disabled={installing || !selectedEnvironment}
              confirm={selectedEnvironment?.data?.askForConfirmation === true}
              confirmMessage={
                selectedEnvironment?.data?.askForConfirmation === true ? (
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                    <span>
                      Please confirm you want to push this update to{" "}
                      <strong>{selectedEnvironment?.data?.name}</strong>
                    </span>
                  </div>
                ) : "Are you sure?"
              }
            >
              {installing ? (
                <div className="flex items-center gap-2 justify-center">
                  <Spinner variant="button" />
                  <span>Installing...</span>
                </div>
              ) : (
                "Install"
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
